/* eslint-disable @typescript-eslint/no-explicit-any */
import { Level2OuterPageLayout, useIsLargerThanIpadResolution, useSetInPageNavVisible } from "@octopusdeploy/design-system-components";
import { CodeBranchIcon } from "@octopusdeploy/design-system-icons";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import type { BranchSpecifier, ProjectTemplateResource, ValidateGitRefV2Response } from "@octopusdeploy/octopus-server-client";
import { UseDefaultBranch, ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { ReactNode } from "react";
import * as React from "react";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import ErrorContextProvider from "~/components/ErrorContext/ErrorContext";
import ErrorPanel from "~/components/ErrorPanel/ErrorPanel";
import Markdown from "~/components/Markdown";
import { SkeletonLoadingLayout } from "~/components/SkeletonLoadingLayout/SkeletonLoadingLayout";
import { useCreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
import type { CreatePageSideNavLink } from "~/hooks/useCreatePageSideNavLink";
export interface ProjectTemplateLayoutProps {
    slug: string;
    branchName: string;
    isNewlyCreatedProject?: string | null;
    configureProject?: boolean;
    defaultActionType?: string | null;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
    projectStatus: any; // TODO: Import proper type
    children: (params: {
        projectTemplateContext: any; // TODO: Define proper context type
        projectStatus: any;
    }) => React.ReactNode;
}
export function ProjectTemplateLayout({ slug, branchName, isNewlyCreatedProject, configureProject, defaultActionType, scrollAreaRef, projectStatus, children }: ProjectTemplateLayoutProps) {
    const createPageSideNavLink = useCreatePageSideNavLink();
    return (<ErrorContextProvider>
            <ProjectTemplateLayoutInternal slug={slug} branchName={branchName} isNewlyCreatedProject={isNewlyCreatedProject} configureProject={configureProject} defaultActionType={defaultActionType} scrollAreaRef={scrollAreaRef} projectStatus={projectStatus} createPageSideNavLink={createPageSideNavLink}>
                {children}
            </ProjectTemplateLayoutInternal>
        </ErrorContextProvider>);
}
type ProjectTemplateLayoutInternalProps = {
    slug: string;
    branchName: string;
    isNewlyCreatedProject: string | null | undefined;
    configureProject: boolean | null | undefined;
    defaultActionType?: string | null | undefined;
    scrollAreaRef: React.RefObject<HTMLDivElement>;
    projectStatus: any;
    createPageSideNavLink: CreatePageSideNavLink;
    children: (params: {
        projectTemplateContext: any;
        projectStatus: any;
    }) => ReactNode;
};
interface ProjectTemplateLayoutState extends DataBaseComponentState {
    projectTemplate: ProjectTemplateResource | null;
    gitRefValidationError: ValidateGitRefV2Response | null;
}
class ProjectTemplateLayoutInternal extends DataBaseComponent<ProjectTemplateLayoutInternalProps, ProjectTemplateLayoutState> {
    constructor(props: ProjectTemplateLayoutInternalProps) {
        super(props);
        this.state = {
            projectTemplate: null,
            gitRefValidationError: null,
        };
    }
    async componentDidMount() {
        // For now, create a mock project template to avoid data loading issues
        // TODO: Implement proper project template loading
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const mockProjectTemplate: ProjectTemplateResource = {
            Id: this.props.slug,
            Name: this.props.slug,
            Slug: this.props.slug,
            Description: "Project template description",
        } as ProjectTemplateResource;
        this.setState({
            projectTemplate: mockProjectTemplate,
            gitRefValidationError: null,
        });
    }
    toBranchSpecifier(branch: string | undefined): BranchSpecifier {
        return branch ? branch : UseDefaultBranch;
    }
    render() {
        const branchSpecifier = this.toBranchSpecifier(this.props.branchName);
        if (!this.state.projectTemplate) {
            return (<main>
                    <HideAreaNavPanelWorkaround />
                    <SkeletonLoadingLayout errors={this.errors}/>
                </main>);
        }
        // Create mock project template context
        const projectTemplateContext = {
            state: {
                model: this.state.projectTemplate,
                gitRefValidationError: this.state.gitRefValidationError,
            },
        };
        const navigationSidebarContent = this.state.gitRefValidationError?.Type === ValidateGitRefV2ResponseType.ConnectionFailed
            ? this.getGitErrorPanel(this.state.gitRefValidationError)
            : this.props.children({
                projectTemplateContext,
                projectStatus: this.props.projectStatus,
            });
        return (<main>
                {this.renderErrors()}
                <Level2OuterPageLayout navItems={this.getProjectTemplateNavItems(this.state.projectTemplate)} header={{
                breadcrumbs: this.headerBreadcrumbs(),
                title: this.state.projectTemplate.Name,
                titleIcon: <ProjectTemplatePageHeaderTitleIcon />,
            }} description={<Markdown markup={this.state.projectTemplate.Description ?? ""}/>}>
                    {navigationSidebarContent}
                </Level2OuterPageLayout>
            </main>);
    }
    private getProjectTemplateNavItems(projectTemplate: ProjectTemplateResource) {
        return [
            this.props.createPageSideNavLink("Process", links.phBranchDeploymentProcessPage.generateUrl({ slug: projectTemplate.Slug, gitRef: this.props.branchName }), true),
            this.props.createPageSideNavLink("Variables", links.phBranchVariablesPage.generateUrl({ slug: projectTemplate.Slug, gitRef: this.props.branchName }), true),
        ];
    }
    private getGitErrorPanel(error: ValidateGitRefV2Response) {
        return (<div>
                <h3>Git Reference Error</h3>
                <p>There was an issue with the git reference {error.Message}</p>
            </div>);
    }
    renderErrors() {
        const errors = this.state && this.errors;
        if (!errors) {
            return null;
        }
        return <ErrorPanel message={errors.message} errors={errors.errors} parsedHelpLinks={errors.parsedHelpLinks} helpText={errors.helpText} helpLink={errors.helpLink}/>;
    }
    private headerBreadcrumbs() {
        return [{ label: "Project Templates", pageUrl: links.projectTemplatesPage.generateUrl() }];
    }
    static displayName = "ProjectTemplateLayoutInternal";
}
function ProjectTemplatePageHeaderTitleIcon() {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    return <>{isLargerThanIpad ? <CodeBranchIcon size={20} color={themeTokens.color.icon.tertiary}/> : undefined}</>;
}
/*
Currently the routes we have for branch vs non branch based pages co-exist in the same menu.
This means that the `ProjectTemplateLayout` currently gets unmounted and remounted between these
routes. This is especially noticeable for the vertical navigation as we show the area nav
panel when the in page menu is not visible. This is exactly what happens when we navigate
between a git based page vs a non git based page when vertical navigation is enabled.
This workaround provides us with a cheap way to make this transition less jarring until
we can find a way to properly refactor the layout to either be mounted higher up in the
tree OR we consolidate routes in some way (possibly using URL query parameters instead).
 */
function HideAreaNavPanelWorkaround() {
    const setVisible = useSetInPageNavVisible();
    React.useEffect(() => {
        setVisible?.(true);
        return () => setVisible?.(false);
    }, [setVisible]);
    return null;
}
